import * as yup from "yup";
import { Formik, Form as FormikForm } from "formik";
import { Button as BSButton, FloatingLabel, Form } from "react-bootstrap";
import { CapturePreAuthRequestDTO } from "../../../../data/entities";
import { formatPrice } from "../../../../utils";

export interface CaptureFormProps {
  paymentId: number;
  maxAmount: number;
  onSubmit: (captureRequest: CapturePreAuthRequestDTO) => void;
  submitRef: React.RefObject<HTMLButtonElement>;
}

export function CaptureForm({ paymentId, maxAmount, onSubmit, submitRef }: Readonly<CaptureFormProps>) {
  return (
    <Formik
      enableReinitialize
      initialValues={{
        amount: parseFloat(maxAmount.toFixed(2)),
        reasonOption: "",
        reasonOther: "",
        useOverrideAmount: false,
        confirmAction: false,
      }}
      validationSchema={yup.object().shape({
        amount: yup
          .number()
          .nullable()
          .when("useOverrideAmount", {
            is: true,
            then: (schema) =>
              schema
                .required("Amount is required")
                .min(0.01, "Amount must be greater than 0")
                .max(maxAmount, `Cannot exceed pre-authorized amount of ${formatPrice(maxAmount)}`),
            otherwise: (schema) => schema.notRequired(),
          }),
        reasonOption: yup.string().required("Please select a reason"),
        reasonOther: yup
          .string()
          .when("reasonOption", {
            is: "Other",
            then: (schema) => schema.required("Please specify a reason"),
            otherwise: (schema) => schema.notRequired(),
          }),
        confirmAction: yup
          .boolean()
          .oneOf([true], "You must confirm before proceeding."),
      })}
      onSubmit={(values) => {
        onSubmit({
          paymentId: paymentId,
          overrideAmount: values.useOverrideAmount ? values.amount : null,
          captureReason: values.reasonOption === "Other" ? values.reasonOther : values.reasonOption,
        });
      }}
    >
      {({ handleSubmit, values, setFieldValue, touched, errors }) => (
        <FormikForm noValidate onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1">
          {/* Reason for capture */}
          <div className="mb-3">
            <Form.Label>Reason for capture</Form.Label>
            <Form.Select
              value={values.reasonOption}
              onChange={(e) => setFieldValue("reasonOption", e.target.value)}
              isInvalid={touched.reasonOption && !!errors.reasonOption}
            >
              <option value="">Select reason</option>
              <option value="Service Completed">Service Completed</option>
              <option value="Missed Appointment">Missed Appointment</option>
              <option value="Other">Other</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.reasonOption}</Form.Control.Feedback>
          </div>
          
          {values.reasonOption === "Other" && (
            <FloatingLabel label="Specify reason" className="mb-3">
              <Form.Control
                as="textarea"
                type="text"
                value={values.reasonOther}
                style={{ minHeight: "150px" }}
                onChange={(e) => setFieldValue("reasonOther", e.target.value)}
                isInvalid={touched.reasonOther && !!errors.reasonOther}
              />
              <Form.Control.Feedback type="invalid">{errors.reasonOther}</Form.Control.Feedback>
            </FloatingLabel>
          )}

          {/* Override amount */}
          <div className="mb-3 mt-2">
            <Form.Check
              type="switch"
              id="override-amount-switch"
              label="Override amount"
              checked={values.useOverrideAmount}
              onChange={() => setFieldValue("useOverrideAmount", !values.useOverrideAmount)}
            />
            {values.useOverrideAmount && (
              <div className="mt-3">
                <FloatingLabel label={`Amount to capture (max ${formatPrice(maxAmount)})`}>
                  <Form.Control
                    type="number"
                    min={0}
                    max={maxAmount}
                    step="0.01"
                    value={values.amount ?? ""}
                    onChange={(e) =>{
                        const value = e.target.value === "" ? null : parseFloat(parseFloat(e.target.value).toFixed(2));
                        setFieldValue("amount", value);
                    }}
                    isInvalid={touched.amount && !!errors.amount}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amount}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </div>
            )}
          </div>

          {/* Confirmation checkbox */}
          <div className="mt-auto mb-3 p-3 rounded bg-warning bg-opacity-10 border border-warning">
            <Form.Check
              type="checkbox"
              label={`I confirm capturing ${formatPrice(values.useOverrideAmount ? values.amount ?? 0 : maxAmount)} from this pre-authorization.`}
              checked={values.confirmAction}
              onChange={(e) => setFieldValue("confirmAction", e.target.checked)}
              isInvalid={touched.confirmAction && !!errors.confirmAction}
            />
          </div>

          <BSButton type="submit" className="d-none" ref={submitRef} />
        </FormikForm>
      )}
    </Formik>
  );
}

export default CaptureForm;